import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Loader2, Mail, MapPin, Globe } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/lang";
import { apps, CONTACT_EMAIL } from "@/lib/apps";
import { EASE, Eyebrow, MaskLines, Reveal } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const empty = { name: "", email: "", app: "general", message: "" };

const Field = ({ label, id, textarea, ...props }) => {
  const Tag = textarea ? "textarea" : "input";
  return (
    <label htmlFor={id} className="field block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">{label}</span>
      <Tag id={id} {...props} className="field-input mt-2 w-full resize-none border-b border-white/15 bg-transparent pb-3 text-lg text-white outline-none transition-colors duration-300 placeholder:text-white/20 focus:border-[#22d3ee]" />
    </label>
  );
};

const Meta = ({ Icon, label, value, href, testid }) => (
  <div className="flex items-center gap-4 border-b border-white/10 py-5">
    <Icon className="h-4 w-4 text-[#5cf2a0]" />
    <span className="w-24 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</span>
    {href ? <a href={href} data-testid={testid} className="link-underline text-white/85">{value}</a> : <span className="text-white/85">{value}</span>}
  </div>
);

export const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(empty);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const options = [["general", t.contact.general], ...apps.map((a) => [a.slug, a.name])];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !EMAIL_RE.test(form.email) || form.message.trim().length < 5) {
      toast.error(t.contact.invalid);
      return;
    }
    setState("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      setState("sent");
      setForm(empty);
      toast.success(t.contact.success);
    } catch {
      setState("idle");
      toast.error(t.contact.error);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-36">
      <div className="container-x grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Eyebrow>{t.contact.eyebrow}</Eyebrow>
          <h2 key={lang} data-testid="contact-title" className="mt-6 font-display text-7xl font-extrabold leading-[0.85] tracking-[-0.05em] sm:text-8xl lg:text-[9rem]">
            <MaskLines inView lines={t.contact.titleLines} lineClassName={(i) => (i === 1 ? "text-z-gradient" : "")} />
          </h2>
          <Reveal as="p" className="mt-8 max-w-md text-base leading-relaxed text-white/60 sm:text-lg">{t.contact.sub}</Reveal>
          <Reveal className="mt-10 border-t border-white/10">
            <Meta Icon={Mail} label="E-mail" value={CONTACT_EMAIL} href={`mailto:${CONTACT_EMAIL}`} testid="contact-email-link" />
            <Meta Icon={MapPin} label={t.contact.location} value={t.contact.locationValue} />
            <Meta Icon={Globe} label={t.contact.web} value="www.zenvio.dk" />
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="glass relative overflow-hidden rounded-[32px] p-6 sm:p-10">
            <AnimatePresence mode="wait">
              {state === "sent" ? (
                <motion.div key="sent" data-testid="contact-success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6, ease: EASE }} className="flex min-h-[460px] flex-col items-start justify-center">
                  <motion.span initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.1 }} className="grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-[#22d3ee] to-[#5cf2a0] text-[#05070f]">
                    <Check className="h-7 w-7" />
                  </motion.span>
                  <h3 className="mt-8 font-display text-4xl font-bold tracking-tight">{t.contact.success}</h3>
                  <p className="mt-3 text-white/60">{t.contact.successSub}</p>
                  <button type="button" data-testid="contact-send-another-button" onClick={() => setState("idle")} className="btn-ghost mt-8">{t.contact.again}</button>
                </motion.div>
              ) : (
                <motion.form key="form" data-testid="contact-form" onSubmit={submit} noValidate initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-9">
                  <div className="grid gap-9 sm:grid-cols-2">
                    <Field id="c-name" label={t.contact.name} value={form.name} onChange={set("name")} data-testid="contact-input-name" autoComplete="name" />
                    <Field id="c-email" type="email" label={t.contact.email} value={form.email} onChange={set("email")} data-testid="contact-input-email" autoComplete="email" />
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">{t.contact.app}</span>
                    <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" data-testid="contact-select-app">
                      {options.map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          role="radio"
                          aria-checked={form.app === val}
                          data-testid={`contact-app-chip-${val}`}
                          onClick={() => setForm((f) => ({ ...f, app: val }))}
                          className={`rounded-full border px-4 py-2 text-sm transition-[background-color,color,border-color,transform] duration-300 active:scale-95 ${form.app === val ? "border-transparent bg-white text-[#05070f]" : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Field id="c-message" textarea rows={5} label={t.contact.message} value={form.message} onChange={set("message")} data-testid="contact-textarea-message" />
                  <button type="submit" disabled={state === "sending"} data-testid="contact-submit-button" className="btn-primary group w-full justify-center !py-4 text-base disabled:opacity-60 sm:w-auto">
                    {state === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {state === "sending" ? t.contact.sending : t.contact.submit}
                    {state !== "sending" && <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
